import { Mail, MapPin, Phone } from 'lucide-react';
import type { LeadFormConfig, LeadFormField } from '../../../lib/crm/leadForms';

type BusinessBits = {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  logoUrl?: string | null;
};

type Props = {
  title: string;
  config: LeadFormConfig;
  fields: LeadFormField[];
  business?: BusinessBits;
};

function PreviewField({
  field,
  textColor,
  accentColor,
}: {
  field: LeadFormField;
  textColor: string;
  accentColor: string;
}) {
  if (field.type === 'customer_info') {
    return (
      <div className="mb-5">
        <p className="text-[13px] font-semibold mb-2" style={{ color: textColor }}>
          Your Information <span className="text-red-500">*</span>
        </p>
        <div className="space-y-2 p-3 rounded-lg bg-black/[0.03] border border-black/5">
          <div className="grid grid-cols-2 gap-2">
            <div className="h-8 rounded-md border border-black/10 bg-white px-2 text-[11px] text-gray-400 flex items-center">
              First name
            </div>
            <div className="h-8 rounded-md border border-black/10 bg-white px-2 text-[11px] text-gray-400 flex items-center">
              Last name
            </div>
          </div>
          <div className="h-8 rounded-md border border-black/10 bg-white px-2 text-[11px] text-gray-400 flex items-center">
            Email
          </div>
          <div className="h-8 rounded-md border border-black/10 bg-white px-2 text-[11px] text-gray-400 flex items-center">
            Phone
          </div>
        </div>
      </div>
    );
  }

  if (field.type === 'checkbox') {
    return (
      <div className="mb-5">
        <p className="text-[13px] font-medium mb-2" style={{ color: textColor }}>
          {field.label}
          {field.mandatory ? <span className="text-red-500 ml-0.5">*</span> : null}
        </p>
        <div className="space-y-1.5">
          {(field.options || []).map((opt) => (
            <div key={opt.id} className="flex items-center gap-2 text-[12px]" style={{ color: textColor }}>
              <span
                className="h-3.5 w-3.5 rounded border"
                style={{ borderColor: accentColor, opacity: 0.5 }}
              />
              {opt.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="mb-5">
        <p className="text-[13px] font-medium mb-2" style={{ color: textColor }}>
          {field.label}
          {field.mandatory ? <span className="text-red-500 ml-0.5">*</span> : null}
        </p>
        <div className="h-14 rounded-md border border-black/10 bg-white px-2 py-1.5 text-[11px] text-gray-400">
          {field.placeholder || '…'}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-5">
      <p className="text-[13px] font-medium mb-2" style={{ color: textColor }}>
        {field.label}
        {field.mandatory ? <span className="text-red-500 ml-0.5">*</span> : null}
      </p>
      <div className="h-8 rounded-md border border-black/10 bg-white px-2 text-[11px] text-gray-400 flex items-center">
        {field.placeholder || '…'}
      </div>
    </div>
  );
}

export default function LeadFormLivePreview({ title, config, fields, business }: Props) {
  const bg = config.background_color || '#ffffff';
  const textColor = config.text_color || '#111827';
  const headerColor = config.header_color || '#000000';
  const buttonColor = config.button_color || '#2563eb';
  const showHeader = config.show_header !== false;

  return (
    <div className="flex justify-center">
      <div
        className="w-full max-w-[320px] rounded-[28px] border-[10px] border-gray-900 shadow-xl overflow-hidden bg-gray-900"
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        }}
      >
        <div className="bg-gray-900 h-6 flex items-center justify-center">
          <div className="w-16 h-1.5 rounded-full bg-gray-700" />
        </div>
        <div className="h-[520px] overflow-y-auto" style={{ backgroundColor: bg }}>
          {showHeader ? (
            <div className="px-4 py-4" style={{ backgroundColor: headerColor }}>
              <div className="flex items-center gap-3">
                {business?.logoUrl ? (
                  <img
                    src={business.logoUrl}
                    alt=""
                    className="h-10 w-10 rounded-lg object-cover bg-white/10"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-lg bg-white/15" />
                )}
                <div className="min-w-0">
                  {config.show_business_name !== false && business?.name ? (
                    <p className="text-[12px] text-white/80 truncate">{business.name}</p>
                  ) : null}
                  <p className="text-[15px] font-semibold text-white truncate">{title || 'Untitled form'}</p>
                </div>
              </div>
              {config.sub_header ? (
                <p className="text-[12px] text-white/75 mt-2">{config.sub_header}</p>
              ) : null}
            </div>
          ) : null}

          <div className="px-4 py-4">
            {config.show_direct_contact ? (
              <div className="mb-4 space-y-1.5 text-[11px]" style={{ color: textColor }}>
                {config.show_phone && business?.phone ? (
                  <div className="flex items-center gap-1.5 opacity-80">
                    <Phone className="h-3 w-3" />
                    {business.phone}
                  </div>
                ) : null}
                {config.show_email && business?.email ? (
                  <div className="flex items-center gap-1.5 opacity-80">
                    <Mail className="h-3 w-3" />
                    {business.email}
                  </div>
                ) : null}
                {config.show_address && business?.address ? (
                  <div className="flex items-center gap-1.5 opacity-80">
                    <MapPin className="h-3 w-3" />
                    {business.address}
                  </div>
                ) : null}
              </div>
            ) : null}

            {fields.length === 0 ? (
              <p className="text-[13px] text-center py-10 opacity-50" style={{ color: textColor }}>
                Add fields to preview your form
              </p>
            ) : (
              fields.map((field) => (
                <PreviewField
                  key={field.id}
                  field={field}
                  textColor={textColor}
                  accentColor={buttonColor}
                />
              ))
            )}

            <div
              className="mt-2 w-full rounded-lg py-2.5 text-center text-[13px] font-semibold text-white"
              style={{ backgroundColor: buttonColor }}
            >
              {config.button_text?.trim() || 'Submit'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
